import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Progress, Typography, Button, Alert, Spin, Tabs, Tag } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, SyncOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { smartReviewService } from '../services/smartReviewService';
import { updateUploadStatus } from '../store/uploadHistorySlice';
import { FlowStatusResponse } from '../types';
import type { AppDispatch } from '../store/store';
import { NimbusLogo } from './NimbusLogo';
import { FileGroupTabs } from './FileGroupTabs';
import './FileUpload.css';

const POLLING_INTERVAL_MS = 4000;

const computeProgress = (status: FlowStatusResponse) => {
  const completedFiles = status.files.length;
  const completionPercentage = status.total_files > 0
    ? Math.round((completedFiles / status.total_files) * 100)
    : status.status === 'completed' ? 100 : 0;
  return { completedFiles, completionPercentage };
};

const { Title, Text } = Typography;

export const PollingStatusPage: React.FC = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const [flowStatus, setFlowStatus] = useState<FlowStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const pollFlowStatus = useCallback(async () => {
    if (!flowId || isPolling || !isMountedRef.current) return;
    
    setIsPolling(true);
    
    try {
      const response = await smartReviewService.getFlowStatus(flowId);
      setFlowStatus(response);
      setError(null);
      
      // Update Redux store
      const progress = computeProgress(response);
      dispatch(updateUploadStatus({
        flowId: response.flow_id,
        status: response.status,
        completionPercentage: progress.completionPercentage,
        completedFiles: progress.completedFiles,
        totalFiles: response.total_files,
        errorMessage: response.error_message,
        success: response.status === 'completed'
      }));

      // Decide next action based on status
      if (response.status === 'completed' || response.status === 'failed') {
        setPolling(false);
      } else if (response.status === 'processing') {
        if (isMountedRef.current && polling) {
          timeoutRef.current = setTimeout(pollFlowStatus, POLLING_INTERVAL_MS);
        }
      }

    } catch (err: any) {
      console.error('Error polling flow status:', err);
      setError(err?.response?.data?.detail || 'Failed to check status');
      // On error, still try to poll again after the polling interval
      if (isMountedRef.current && polling) {
        timeoutRef.current = setTimeout(pollFlowStatus, POLLING_INTERVAL_MS);
      }
    } finally {
      setLoading(false);
      setIsPolling(false);
    }
  }, [flowId, isPolling, polling, dispatch]);

  const pollFlowStatusRef = useRef(pollFlowStatus);
  pollFlowStatusRef.current = pollFlowStatus;

  useEffect(() => {
    if (!flowId) return;

    isMountedRef.current = true;

    // Start polling immediately
    pollFlowStatusRef.current();

    // Cleanup function - stops polling when component unmounts
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [flowId]);

  // Stop polling when leaving the page
  useEffect(() => {
    return () => {
      setPolling(false);
    };
  }, []);

  // Ask notification permission once, restore tab title on unmount
  const originalTitleRef = useRef(document.title);
  const notifiedRef = useRef(false);
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const originalTitle = originalTitleRef.current;
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // Keep the tab title in sync and notify when processing finishes
  useEffect(() => {
    if (!flowStatus) return;

    if (flowStatus.status === 'processing') {
      const { completionPercentage } = computeProgress(flowStatus);
      document.title = `(${completionPercentage}%) Processing… — Smart Review`;
    } else if (flowStatus.status === 'completed') {
      document.title = '✓ Completed — Smart Review';
    } else if (flowStatus.status === 'failed') {
      document.title = '✗ Failed — Smart Review';
    }

    // Notify only on the processing → completed/failed transition observed on
    // this page (not when opening an already-finished flow)
    const finishedNow =
      prevStatusRef.current === 'processing' &&
      (flowStatus.status === 'completed' || flowStatus.status === 'failed');
    prevStatusRef.current = flowStatus.status;

    if (finishedNow && !notifiedRef.current) {
      notifiedRef.current = true;
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(
          flowStatus.status === 'completed' ? 'Processing completed' : 'Processing failed',
          {
            body:
              flowStatus.status === 'completed'
                ? `${flowStatus.filename}: all ${flowStatus.total_files} files are ready.`
                : `${flowStatus.filename}: ${flowStatus.error_message || 'processing failed.'}`,
          }
        );
      }
    }
  }, [flowStatus]);

  const handleGoBack = () => {
    setPolling(false);
    navigate('/');
  };


  if (loading && !flowStatus) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !flowStatus) {
    // Navigate to home after a short delay when status fails to load
    setTimeout(() => {
      navigate('/');
    }, 2000);
    
    return (
      <Card style={{ maxWidth: 600, margin: '50px auto' }}>
        <Alert
          message="Error Loading Status"
          description="Unable to load processing status. Redirecting to home page..."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (!flowStatus) {
    return (
      <Card style={{ maxWidth: 600, margin: '50px auto' }}>
        <Alert
          message="Flow Not Found"
          description="The requested processing flow could not be found."
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={handleGoBack}>
              <ArrowLeftOutlined /> Go Back
            </Button>
          }
        />
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'exception';
      case 'processing': return 'active';
      default: return 'normal';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'processing': return 'Processing';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  const { completedFiles, completionPercentage } = computeProgress(flowStatus);

  const statusContent = (
    <>
      {/* Progress Section */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ marginBottom: 24 }}>
              <Progress
                type="circle"
                percent={completionPercentage}
                status={getStatusColor(flowStatus.status)}
                strokeWidth={8}
                size={160}
                format={() => (
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, marginBottom: 4 }}>
                      {completionPercentage}%
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      Complete
                    </div>
                  </div>
                )}
              />
            </div>
            
            <div style={{ marginBottom: 20 }}>
              <Title level={3} style={{ margin: 0, marginBottom: 8 }}>
                {getStatusText(flowStatus.status)}
              </Title>
              <Text type="secondary" style={{ fontSize: 16 }}>
                {completedFiles} of {flowStatus.total_files} files processed
              </Text>
              {flowStatus.status === 'processing' && flowStatus.current_entity && (
                <div style={{ marginTop: 12 }}>
                  <Tag icon={<SyncOutlined spin />} color="processing" style={{ fontSize: 14, padding: '4px 12px' }}>
                    Currently processing: {flowStatus.current_entity}
                    {flowStatus.current_category && (
                      flowStatus.current_category === 'firm' ? ' (Firm)' : ' (Marketing Source)'
                    )}
                  </Tag>
                </div>
              )}
              {flowStatus.status === 'processing' && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontStyle: 'italic' }}>
                    This may take a few minutes
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Processing Details */}
          <div>
            <Text strong style={{ fontSize: 16, marginBottom: 16, display: 'block' }}>
              Processing Details
            </Text>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: 16,
              marginBottom: 20 
            }}>
              <div style={{ 
                padding: 16, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 6,
                textAlign: 'center',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#1890ff', marginBottom: 4 }}>
                  {flowStatus.total_firms}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>Total Firms</Text>
              </div>
              <div style={{ 
                padding: 16, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 6,
                textAlign: 'center',
                border: '1px solid #e8e8e8'
              }}>
                <div style={{ fontSize: 20, fontWeight: 600, color: '#52c41a', marginBottom: 4 }}>
                  {flowStatus.total_marketing_sources}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>Marketing Sources</Text>
              </div>
            </div>
            
            <div style={{ 
              backgroundColor: '#fafafa', 
              padding: 16, 
              borderRadius: 6,
              border: '1px solid #e8e8e8',
              marginBottom: 16 
            }}>
              <div style={{ marginBottom: 8 }}>
                <Text>Started At: </Text>
                <Text strong>
                  {flowStatus.started_at ? new Date(flowStatus.started_at).toLocaleString() : 'Not started'}
                </Text>
              </div>
              {flowStatus.completed_at && (
                <div>
                  <Text>Completed At: </Text>
                  <Text strong>{new Date(flowStatus.completed_at).toLocaleString()}</Text>
                </div>
              )}
            </div>

            {flowStatus.status === 'failed' && flowStatus.error_message && (
              <Alert
                message="Processing Failed"
                description={
                  flowStatus.files.length > 0
                    ? `${flowStatus.error_message} — files completed before the failure are still available in the Results tab.`
                    : flowStatus.error_message
                }
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {flowStatus.status === 'completed' && (
              <Alert
                message="Processing Completed!"
                description="All files are ready. You can download them from the Results tab or open the full results page."
                type="success"
                showIcon
                action={
                  <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/upload/${flowId}`)}
                  >
                    View full results
                  </Button>
                }
                style={{ marginBottom: 16 }}
              />
            )}
          </div>
    </>
  );

  const firmFiles = flowStatus.files.filter((f) => f.category === 'firm');
  const marketingFiles = flowStatus.files.filter((f) => f.category === 'marketingsource');

  const resultsContent = (
    <div style={{ marginTop: 8 }}>
      <FileGroupTabs
        title="FIRM Analysis"
        files={firmFiles}
        expectedTotal={flowStatus.total_firms}
        processingEntity={flowStatus.current_category === 'firm' ? flowStatus.current_entity : undefined}
      />
      <FileGroupTabs
        title="MARKETING SOURCE Analysis"
        files={marketingFiles}
        expectedTotal={flowStatus.total_marketing_sources}
        processingEntity={flowStatus.current_category === 'marketingsource' ? flowStatus.current_entity : undefined}
      />
    </div>
  );

  return (
    <div className="file-upload-container">
      <Card
        title={
          <div className="file-upload-title-container">
            <div className="top-left-header mt-3" style={{height:110}}>
              <NimbusLogo className="file-upload-logo" />
            </div>
          </div>
        }
        className="file-upload-card"
      >

        <div className="file-upload-content">
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ margin: 0, color: '#000' }}>
              {flowStatus.filename || 'Processing file...'}
            </Title>
          </div>
          <Text type="secondary" className="file-upload-subtitle" style={{paddingTop:0, marginTop:0}}>
            Processing Status - Flow ID: {flowId}
          </Text>
          <Tabs
            defaultActiveKey="status"
            items={[
              {
                key: 'status',
                label: 'Status',
                children: statusContent,
              },
              {
                key: 'results',
                label: `Results (${flowStatus.files.length})`,
                disabled: flowStatus.files.length === 0,
                children: resultsContent,
              },
            ]}
          />
        </div>
      </Card>
    </div>
  );
};